import {addCircularTabFocus, focusElement} from '@wix/panda-js-utils'
import {ExperimentNames} from '@wix/wix-events-commons-statics'
import {useExperiments, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useLayoutEffect} from 'react'
import {useAppActions, useAppHost, useAppSelector} from '../../../hooks/runtime'
import {isInAccessibilityMode} from '../../../selectors/seating/mode'
import {CloseButton} from '../common/close-button'
import {Header} from '../common/header'
import {ModalProps} from './interfaces'
import s from './modal.scss'

export const Modal = ({
  children,
  skin,
  closeButtonAsButton,
  className,
  dataHook,
  firstChildSelector,
  lastChildSelector,
  returnFocusElement,
  title,
  subtitle,
  withoutPadding,
}: ModalProps) => {
  const {id} = useAppHost()
  const {experiments} = useExperiments()
  const {t} = useTranslation()
  const {closeNativeModal} = useAppActions()

  const showAccessibilityMode = useAppSelector(isInAccessibilityMode)
  const grey = skin === 'grey'
  const fixSeatingModalPositionEnabled = experiments.enabled(ExperimentNames.FixSeatingModalPosition)

  useLayoutEffect(() => {
    if (firstChildSelector && lastChildSelector) {
      addCircularTabFocus(firstChildSelector, lastChildSelector)
      focusElement({selector: firstChildSelector, canFocus: true})
    }

    const container = fixSeatingModalPositionEnabled ? document.getElementById(id) : undefined
    const containerTransform = container ? getComputedStyle(container).transform : undefined

    if (containerTransform) {
      container.style.transform = 'none'
    }

    return () => {
      if (returnFocusElement) {
        focusElement({selector: returnFocusElement, canFocus: true})
      }

      if (containerTransform) {
        container.style.removeProperty('transform')
      }
    }
  }, [])

  return (
    <>
      <div className={s.underlay} />
      <div
        className={classNames(s.container, className, {
          [s.grey]: grey,
          [s.noPadding]: !!title || withoutPadding,
        })}
        data-hook={dataHook}
        role="dialog"
        aria-modal="true"
        aria-labelledby={t('seatings_mobile_modal_eventName_aria')}
        aria-describedby={t('seatings_mobile_modal_mapView_aria')}
        style={{paddingBottom: !!title || withoutPadding || grey ? 0 : 52}}
        onKeyDown={e => {
          e.stopPropagation()
          if (e.key === 'Escape') {
            closeNativeModal()
          }
        }}
      >
        <div id={t('seatings_mobile_modal_mapView_aria')} style={{display: 'none'}}>
          {t('seatings_mobile_modal_mapView_aria')}
        </div>
        {!!title && !showAccessibilityMode && (
          <Header
            title={title}
            subtitle={subtitle}
            weight="light"
            alignment="left"
            closeButtonAsButton={closeButtonAsButton}
            closeModal={closeNativeModal}
          />
        )}
        {children}
        {!title && <CloseButton closeButtonAsButton={closeButtonAsButton} closeModal={closeNativeModal} />}
      </div>
    </>
  )
}
